import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../utils/Loader";
import useCsr from "../hooks/useCsr";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import SidebarMain from "./SidebarMain";
import SidebarCSR from "./SidebarCSR";
import Footer from "./Footer";
import { CButton } from "@coreui/react";
import { ReactComponent as CloseMenu } from "../assets/images/icons/close.svg";
import { ReactComponent as ArrowRight } from "../assets/images/icons/arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../assets/images/icons/arrow-left.svg";
import SidebarMainLogo from "../assets/images/logos/TriloDocs-Logo-Horizontal-POSITIVE.png";
import { useAuth0 } from "@auth0/auth0-react";

const DashboardContent = ({
  sidebarCSR,
  headerTitle,
  headerButton,
  pageContent,
  prevButton,
  nextButton,
  saveButton,
  saveButtonHandler,
  saveButtonDisabled = false,
  ignoreSaveErrors = false,
  nextButtonRightArrow = true,
}) => {
  const { user } = useAuth0();
  const { ajaxLoading } = useCsr();
  const { isPathAllowed } = useFeatureFlags();
  const navigate = useNavigate();
  // Custom navigation handler that first saves then navigates
  const handleNavigationWithSave = async targetPath => {
    if (saveButtonHandler) {
      try {
        // Show success toast and navigate after successful save

        const syntheticEvent = { preventDefault: () => {} };

        // Call the save handler and wait for it to complete
        const result = saveButtonHandler(syntheticEvent);
        let resolved;

        if (result && typeof result.then === "function") {
          resolved = await result;
        }

        // Only navigate if the save handler completed successfully
        // (if it returned undefined, validation failed; if resolved?.success === false, save failed silently)
        if (result !== undefined && !(resolved && resolved.success === false)) {
          navigate(targetPath);
          // Create a synthetic event for the save handler
          setTimeout(() => {
            toast.success("Data saved successfully!");
          }, 100);
        }
      } catch (error) {
        // If ignoreSaveErrors, stay on page with no error display
        if (ignoreSaveErrors) return;
        navigate(targetPath);
        console.warn(
          "Save failed during navigation, but proceeding with navigation:",
          error
        );
        setTimeout(() => {
          toast.error("Save failed, click on Save button to save changes.");
        }, 100);
      }
    } else {
      // If no save handler, just navigate
      navigate(targetPath);
    }
  };

  return (
    <div>
      <div className={"top"}>
        <div className={"logo"}>
          <img src={SidebarMainLogo} width="280px" alt="TriloDocs Logo" />
        </div>
        <div>
          <h6 className="welcome-title">Welcome {user?.name || "User"}</h6>
        </div>
      </div>
      <div>
        <div
          className={`dashboard container-wrapper ${sidebarCSR ? "container-wrapper__three" : ""}`}>
          <SidebarMain />
          {sidebarCSR && <SidebarCSR />}
          <div className={"container-wrapper__innner-container"}>
            <div className={"container-wrapper__inner"}>
              {headerTitle && (
                <div className={"dashboard-header dashboard-header__wrapper"}>
                  <div className={"dashboard-header__left"}>
                    {/* <div className={"eyebrow"}>{headerEyebrow}</div> */}
                    <div className="headerTitleText">{headerTitle}</div>
                  </div>
                  <div className={"dashboard-header__right"}>
                    <>
                      {headerButton === "new-csr" ? (
                        <>
                          <Link
                            to={"/csr/new/summary"}
                            className={
                              "input-submit input-submit__primary input-submit__small"
                            }>
                            Create CSR
                          </Link>
                          <Link
                            to={"/ls/new/select-study"}
                            className={
                              "input-submit input-submit__primary input-submit__small"
                            }>
                            Create Lay Summary
                          </Link>
                        </>
                      ) : (
                        <Link to={"/dashboard"} className={"close-button"}>
                          <CloseMenu />
                          Close
                        </Link>
                      )}
                    </>
                  </div>
                </div>
              )}
              <div
                className={`dashboard-content__wrapper ${(prevButton || nextButton) && "extra-footer"}`}>
                {pageContent}
              </div>
              {(prevButton || nextButton) && (
                <div
                  className={`footer__navigation ${prevButton && nextButton ? "both-buttons" : ""} ${prevButton && !nextButton ? "only-prev-button" : ""}`}>
                  {prevButton && isPathAllowed(prevButton.buttonGoToAction) && (
                    <button
                      onClick={() => navigate(prevButton.buttonGoToAction)}
                      className="input-submit input-submit__secondary input-submit__arrow">
                      <ArrowLeft />
                      {prevButton.buttonTitle}
                    </button>
                  )}
                  {saveButton && (
                    <CButton
                      color={"primary"}
                      variant={"outline"}
                      disabled={saveButtonDisabled}
                      onClick={
                        ignoreSaveErrors
                          ? async e => {
                              try {
                                const result = saveButtonHandler(e);
                                if (result?.then) await result;
                              } catch {
                                /* silently ignore save errors */
                              }
                            }
                          : e => saveButtonHandler(e)
                      }>
                      Save
                    </CButton>
                  )}
                  {nextButton &&
                    isPathAllowed(nextButton.buttonGoToAction) &&
                    (nextButton.buttonFormAction ? (
                      <button
                        form={nextButton.buttonFormAction}
                        className="input-submit input-submit__primary input-submit__arrow"
                        disabled={nextButton.disabled}>
                        {nextButton.buttonTitle}
                        <ArrowRight />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!nextButton.disabled) {
                            handleNavigationWithSave(
                              nextButton.buttonGoToAction
                            );
                          }
                        }}
                        className="input-submit input-submit__primary input-submit__arrow"
                        disabled={nextButton.disabled}>
                        {nextButton.buttonTitle}
                        {nextButtonRightArrow ? <ArrowRight /> : ""}
                      </button>
                    ))}
                </div>
              )}
              <Footer />
              <Loader loading={ajaxLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
